"use strict";
/**
 * Slot value resolution + validation — Single source of truth.
 *
 * Every consumer (runner input-binder, save-time validator, editor preview)
 * goes through these two functions so the rules for "what is the effective
 * value of this slot?" and "is a user-typed constant admissible?" cannot
 * drift between layers.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MissingRequiredFieldError = void 0;
exports.resolveFieldValue = resolveFieldValue;
exports.validateSlotValue = validateSlotValue;
/**
 * Thrown when a `required: true` slot has no mapping, no `value`, and no
 * `defaultValue`. The runner catches and surfaces this as a step error.
 */
class MissingRequiredFieldError extends Error {
    constructor(slot) {
        super(`Required field '${slot.name}' (slot ${slot.id}) has no mapped value, ` +
            `no user-typed constant, and no schema default.`);
        this.slot = slot;
        this.name = 'MissingRequiredFieldError';
    }
}
exports.MissingRequiredFieldError = MissingRequiredFieldError;
/**
 * Resolve the effective value of a slot given any mapped upstream value.
 *
 * Priority:
 *   1. `mappedValue` if not `undefined` — user bound the slot to an upstream output
 *   2. `slot.value` — user typed a constant in the editor
 *   3. `slot.defaultValue` — schema default from the descriptor
 *   4. Error if `required`, else `undefined`
 *
 * `null` is treated as a legitimate value at every level (so users can
 * explicitly opt-out of a feature). Only `undefined` counts as "not set".
 */
function resolveFieldValue(slot, mappedValue) {
    if (mappedValue !== undefined)
        return mappedValue;
    if (slot.value !== undefined)
        return slot.value;
    if (slot.defaultValue !== undefined)
        return slot.defaultValue;
    if (slot.required)
        throw new MissingRequiredFieldError(slot);
    return undefined;
}
/**
 * Validate a user-typed `value` against the slot's schema. Returns an
 * error list (empty if OK). Does NOT check `required` — that's the
 * runtime resolver's job.
 *
 * Called at workflow-save-time from the backend snapshot validator so a
 * rejected enum cannot even enter the snapshot.
 */
function validateSlotValue(slot) {
    const errors = [];
    if (slot.value === undefined)
        return errors;
    if (slot.type === 'enum') {
        if (!slot.options || slot.options.length === 0) {
            errors.push({
                slotId: slot.id,
                slotName: slot.name,
                reason: 'enum_missing_options',
                message: `Enum slot '${slot.name}' has no options array.`,
            });
            return errors;
        }
        const allowed = slot.options.some(o => o.value === slot.value);
        if (!allowed) {
            errors.push({
                slotId: slot.id,
                slotName: slot.name,
                reason: 'enum_value_not_in_options',
                message: `Enum slot '${slot.name}' value '${String(slot.value)}' is not ` +
                    `in allowed options (${slot.options.map(o => o.value).join(', ')}).`,
            });
        }
        return errors;
    }
    // sl-c9oi.6: `{{steps.X.Y}}` templates are legal on every scalar
    // slot — SlotMapper's row picker writes them when a user binds an
    // upstream output. The runner substitutes them before the executor sees
    // the value. Validator admits template strings as a free pass for
    // boolean/number slots.
    const isBindingTemplate = typeof slot.value === 'string' &&
        /^\{\{\s*steps\.[^.\s]+\.[^}\s]+\s*\}\}$/.test(slot.value);
    if (slot.type === 'boolean' &&
        typeof slot.value !== 'boolean' &&
        !isBindingTemplate) {
        errors.push({
            slotId: slot.id,
            slotName: slot.name,
            reason: 'type_mismatch',
            message: `Boolean slot '${slot.name}' received non-boolean value.`,
        });
    }
    if (slot.type === 'number' &&
        typeof slot.value !== 'number' &&
        !isBindingTemplate) {
        errors.push({
            slotId: slot.id,
            slotName: slot.name,
            reason: 'type_mismatch',
            message: `Number slot '${slot.name}' received non-number value.`,
        });
    }
    if (slot.type === 'text' && typeof slot.value !== 'string') {
        errors.push({
            slotId: slot.id,
            slotName: slot.name,
            reason: 'type_mismatch',
            message: `Text slot '${slot.name}' received non-string value.`,
        });
    }
    return errors;
}
